import * as React from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { client } from 'core/graphql/client';

import { useReactiveVar } from '@apollo/client';
import { authState, LmsRoles } from 'core';
import {
  logout,
  useActiveMenu,
  useCareerShell,
  useGoToErrorPage,
  useLmsAccess,
  useMyCareerCenter,
  useRefreshToken,
  useUser,
} from 'core/hooks';
import { PATHS } from 'router/config';
import { MenuCounter, MenuItems, StudentMenu, TeacherMenu, MethodistMenu, UserRole } from 'context';
import { getToken } from 'services/AuthService';

import { HeaderComponent } from './Header';
import { suspendedStudentMenu, transformMenu } from './transformMenu';

export const Header = memo(() => {
  const { t } = useTranslation();
  const { user: userData, loading, error } = useUser();
  const activeMenu = useActiveMenu();
  const wide = useCareerShell();
  const goToErrorPage = useGoToErrorPage();
  const navigate = useNavigate();
  const { active_role, isDemoUser } = useReactiveVar(authState);
  const { updateTokens } = useRefreshToken();
  // ТЗ 0069: у СТУДЕНТА пункт «Моя карьера» больше не зависит от членства —
  // `/career` стал витриной, и прятать её от того, кому продают, значит
  // показывать её только тем, кому продавать уже нечего.
  //
  // Хук остаётся здесь ради МЕТОДИСТА: его пункты условны по другой причине —
  // без серверного права предпросмотра он ушёл бы на 404 из собственного меню.
  // ТЗ 0074: счётчик неоплаченных фактур и приостановка доступа.
  const { isSuspended, unpaidInvoicesCount } = useLmsAccess();
  // Приостановленному студенту карьерный центр сервер не отдаёт: запрос дал бы
  // LMS_ACCESS_SUSPENDED на каждом монтировании шапки.
  const { isPreview } = useMyCareerCenter({ skip: isSuspended });

  const demoMenu = useMemo(
    () => [
      {
        name: MenuItems.Courses,
        children: `${t('menu.student.courses')}`,
        to: PATHS.courses,
        path: PATHS.courses,
        counter: MenuCounter.groups,
        subMenuCount: userData.groupsCount || 0,
        isActive: MenuItems.Courses === activeMenu,
        disabled: false,
      },
    ],
    [activeMenu, t, userData.groupsCount],
  );

  // 🔴 У методиста карьерные пункты тоже условны. Зашитые намертво, они
  // отправляли бы его на 404 ИЗ СОБСТВЕННОГО меню, если сервер предпросмотр не
  // выдал (флаг выключен, старая версия бэкенда, направление не настроено).
  // У студента такая защита была с самого начала, у методиста — нет.
  const methodistMenu = useMemo(
    () =>
      isPreview ? MethodistMenu : MethodistMenu.filter((item) => item.name === MenuItems.Courses),
    [isPreview],
  );

  const finalMenu = useMemo(() => {
    const counts = { groups: userData.groupsCount, unpaidInvoices: unpaidInvoicesCount };
    const unpaidLabel = t('menu.unpaidInvoicesBadge', { n: unpaidInvoicesCount });
    switch (active_role) {
      case UserRole.STUDENT:
        return transformMenu(
          isSuspended ? suspendedStudentMenu(StudentMenu) : StudentMenu,
          counts,
          activeMenu,
          unpaidLabel,
        );
      case UserRole.TEACHER:
        return transformMenu(TeacherMenu, counts, activeMenu);
      default:
        return transformMenu(methodistMenu, counts, activeMenu);
    }
  }, [
    active_role,
    methodistMenu,
    userData.groupsCount,
    unpaidInvoicesCount,
    isSuspended,
    activeMenu,
    t,
  ]);

  useEffect(() => {
    if (error) {
      console.log({ headerError: error });
      goToErrorPage();
    }
  }, [error, goToErrorPage]);

  const onChangeRole = useCallback(
    async (role: UserRole) => {
      const refresh_token = getToken();
      if (!refresh_token) {
        return;
      }

      const isRoleApplied = await updateTokens(
        refresh_token,
        LmsRoles[role as keyof typeof LmsRoles],
      );
      if (!isRoleApplied) {
        return;
      }

      navigate(PATHS.courses, { replace: true });

      // resetStore, not clearStore: the role lives only in the JWT, so no query
      // variable changes and nothing else would refetch the groups/courses.
      try {
        await client.resetStore();
      } catch (e) {
        // resetStore rejects if any active query fails to refetch — the role
        // has already switched, so this is worth seeing but not worth blocking.
        console.error({ roleSwitchRefetchError: e });
      }
    },
    [updateTokens, navigate],
  );

  return (
    <HeaderComponent
      onExit={logout}
      name={isDemoUser ? t('header.demoUser') : userData.name}
      email={isDemoUser ? '' : userData.email}
      role={userData.role}
      hasRoles={userData.hasRoles}
      onChangeRole={onChangeRole}
      avatarImage={userData.avatarImage}
      menuItems={isDemoUser ? demoMenu : finalMenu}
      loading={loading}
      wide={wide}
    />
  );
});
